"use client";

import type { HoverPoint } from "./types";

interface Props {
  point: HoverPoint;
  /** Position of the active anchor as a percentage of container width (0-100). */
  anchorPct: number;
}

/**
 * Positions via percentage so it scales with the SVG's responsive width.
 * Flips horizontal alignment when anchor is near the container edges so the
 * tooltip body stays inside the viewport.
 */
export function ChartTooltip({ point, anchorPct }: Props) {
  const align = anchorPct > 70 ? "right" : anchorPct < 30 ? "left" : "center";
  const leftStyle =
    align === "right"
      ? { right: `${100 - anchorPct}%`, left: "auto" as const }
      : align === "left"
        ? { left: `${anchorPct}%`, right: "auto" as const }
        : { left: `${anchorPct}%`, transform: "translateX(-50%)" };

  return (
    <div
      data-testid="chart-tooltip"
      data-align={align}
      className="pointer-events-none absolute top-1 z-10 rounded-md border border-surface-light/20 bg-surface/95 px-3 py-2 text-xs shadow-lg backdrop-blur-sm"
      style={leftStyle}
    >
      <div className="mb-1 font-semibold text-foreground">{point.period}</div>
      <ul className="space-y-0.5">
        {point.series.map((s) => (
          <li key={s.label} className="flex items-center gap-2 whitespace-nowrap">
            <span className={`inline-block h-2 w-2 rounded-sm ${s.colorClass}`} aria-hidden="true" />
            <span className="text-muted-foreground">{s.label}</span>
            <span className="ml-auto tabular-nums text-foreground">{s.value == null ? "n/a" : s.format(s.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
